/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, useEffect } from "react";
import useForm from "react-hook-form";
import {
  getRoleOptions,
  emailValidator,
  nameValidator,
} from "../../Authenticator/utils";
import {
  Form,
  Button,
  Dropdown,
  DropdownProps,
  Dimmer,
  Loader,
} from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import cStyles from "../../Components.module.css";
import { Ico } from "../../Icon/Icon";
import { useTranslation } from "react-i18next";
import { ValidationError } from "../../Authenticator";

interface AddUserFormProps {
  onAddUser: any;
  onCancel?: any;
  loading: boolean;
  userRole: string;
  message: string;
}

const AddUserForm: React.FC<AddUserFormProps> = ({
  onAddUser,
  onCancel,
  loading,
  userRole,
  message,
}) => {
  const { t } = useTranslation("common");

  type FormData = {
    name: string;
    email: string;
    role: string;
    [key: string]: any;
  };

  const emptyInputs = {
    name: "",
    email: "",
    role: userRole === "SUPPORT_ADMIN" ? "SUPPORT" : "USER",
  };

  const [inputs, setInputs] = useState<FormData>(emptyInputs);
  const { register, handleSubmit, errors, setValue } = useForm<FormData>();
  useEffect(() => {
    register({ name: "role" }, { required: true });
    setValue("role", inputs.role);
  }, [register, inputs.role, setValue]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const handleDropdownChange = (event: any, data: DropdownProps) => {
    setValue(data.name, data.value);
    setInputs((inputs) => ({ ...inputs, [data.name]: data.value }));
  };

  return (
    <React.Fragment>
      <Dimmer active={loading}>
        <Loader size="large" active={loading} />
      </Dimmer>
      <Form onSubmit={handleSubmit(onAddUser)}>
        <p style={{ marginTop: "0" }}>{message}</p>
        <input type="submit" hidden />

        <Form.Field required error={!!errors.name} style={{ marginBottom: 0 }}>
          <Form.Group inline>
            <label className={styles.myAccountLabel}>{t("full_name")}</label>
            <input
              autoFocus
              name="name"
              key="name"
              className={styles.myAccountInput}
              placeholder={t("full_name")}
              ref={register({
                required: t("name_required") as string,
                pattern: {
                  value: nameValidator,
                  message: t("valid_name"),
                },
              })}
              defaultValue={inputs.name}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Form.Field>

        {!!errors.name && (
          <div
            style={{
              display: "flex",
              marginBottom: "1rem",
            }}
          >
            <span style={{ width: "25%", marginLeft: "20px" }} />
            <ValidationError
              errors={errors}
              name="name"
              pointing="above"
              internal={true}
            />
          </div>
        )}

        <Form.Field inline required error={!!errors.email}>
          <Form.Group inline>
            <label className={styles.myAccountLabel}>Email</label>
            <input
              name="email"
              key="email"
              className={styles.myAccountInput}
              placeholder={t("email")}
              ref={register({ pattern: emailValidator })}
              defaultValue={inputs.email}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Form.Field>

        {!!errors.email && (
          <div
            style={{
              display: "flex",
              marginBottom: "1rem",
            }}
          >
            <span style={{ width: "25%", marginLeft: "20px" }} />
            <ValidationError
              errors={errors}
              name="email"
              pointing="above"
              internal={true}
            />
          </div>
        )}

        <Form.Field inline required error={!inputs.role}>
          <Form.Group inline>
            <label className={styles.myAccountLabel}>{t("role")}</label>
            <Dropdown
              className={styles.myAccountInput}
              name="role"
              key="role"
              fluid
              selection
              options={getRoleOptions(userRole)}
              placeholder={t("select_role")}
              value={inputs.role}
              onChange={handleDropdownChange}
              disabled={!onCancel}
              icon={<Ico name="down" className={styles.dropdownIcon} />}
            />
          </Form.Group>
        </Form.Field>

        <div className={styles.buttonContainer}>
          <Button primary className={cStyles.buttonPrime} type="submit">
            <Ico name="check" style={{ marginRight: "10px" }} />
            {t("invite_user")}
          </Button>

          {onCancel && (
            <Button
              secondary
              className={cStyles.buttonSecond}
              onClick={() => {
                onCancel();
              }}
            >
              <Ico name="cancel" style={{ marginRight: "10px" }} />
              {t("cancel")}
            </Button>
          )}
        </div>
      </Form>
    </React.Fragment>
  );
};

export default AddUserForm;
